import type { Ref } from "react";
import type { UserSchema } from "@common/schemas/user.schema";
import {
    Avatar,
    type AvatarSize,
    type AvatarTone,
    type AvatarVariant,
} from "@design-system/Avatar/Avatar";
import { getUserDisplayName } from "@helpers/user";

interface UserAvatarProps {
    user: Pick<UserSchema, "name" | "avatar" | "iconColor" | "email">;
    size?: AvatarSize;
    /** @default "outline" */
    variant?: AvatarVariant;
    /** `colorful` paints the user's `iconColor`; a photo stays a photo. @default "neutral" */
    tone?: AvatarTone;
    className?: string;
    /** Set when the user's name is visible next to the avatar. */
    "aria-hidden"?: boolean;
    ref?: Ref<HTMLSpanElement>;
}

/**
 * A person's avatar: their photo, else the initial of their name (or email)
 * on a plain ring. `variant="solid" tone="colorful"` gives the coloured look on
 * their assigned `iconColor`. Named after the user for screen readers.
 */
export const UserAvatar = ({
    user,
    size,
    variant = "outline",
    tone = "neutral",
    className,
    "aria-hidden": isAriaHidden,
    ref,
}: Readonly<UserAvatarProps>) => (
    <Avatar
        ref={ref}
        name={getUserDisplayName(user)}
        src={user.avatar}
        variant={variant}
        {...(tone === "neutral" ? { tone } : { tone, color: user.iconColor })}
        size={size}
        className={className}
        aria-hidden={isAriaHidden}
    />
);
